import { useState, useMemo, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

import CircularProgress from '@shared/components/atoms/CircularProgress/CircularProgress';
import {
  MobileApprovalList,
  MobileFilterSheet,
  MobileSelectScreen,
} from '@shared/components/organisms/mobile';

import { getDefaultDateRange } from '@shared/utils/formatDate';

import searchIcon from '@shared/assets/images/search_Icon_mobile.svg';
import searchFilterIcon from '@shared/assets/images/search-filter_Icon.svg';
import chevronDownIcon from '@shared/assets/images/chevron_down_icon.svg';
import closeIcon from '@shared/assets/images/close_icon.svg';
import refreshIcon from '@shared/assets/images/refresh_icon.svg';

import styles from './MobileDrafterContent.module.scss';

/**
 * 모바일 결재자 페이지 콘텐츠
 * - searchParams는 section.setSearchParams로 직접 제어
 * - 모바일에서는 "적용 = 즉시 검색"
 */
function MobileApproverContent({
  activeTab,
  onTabChange,
  approvalIngSection,
  futureIngSection,
  doneSection,
  onItemClick,
  formOptions,
  teamOptions,
  approvalIngCount,
  approvalIngSectionLoading,
}) {
  const [searchValue, setSearchValue] = useState('');
  // 실제 검색 값
  const [appliedSearchValue, setAppliedSearchValue] = useState('');
  const [sortValue, setSortValue] = useState('latest');
  const [isSortOpen, setIsSortOpen] = useState(false);
  const sortDropdownRef = useRef(null);

  // 정렬 드롭다운: 외부 터치/클릭 시 닫기 (모바일 포함)
  useEffect(function () {
    function handlePointerOutside(event) {
      if (sortDropdownRef.current && !sortDropdownRef.current.contains(event.target)) {
        setIsSortOpen(false);
      }
    }

    document.addEventListener('mousedown', handlePointerOutside);
    document.addEventListener('touchstart', handlePointerOutside, { passive: true });
    return function () {
      document.removeEventListener('mousedown', handlePointerOutside);
      document.removeEventListener('touchstart', handlePointerOutside);
    };
  }, []);

  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isSelectOpen, setIsSelectOpen] = useState(false);
  const [selectField, setSelectField] = useState(null);

  const defaultRange = getDefaultDateRange();

  const [filterValues, setFilterValues] = useState({
    date1: defaultRange.start,
    date2: defaultRange.end,
    docType: '',
    team: '',
    title: '',
    name: '',
  });

  const filterFields = useMemo(() => {
    return [
      {
        key: 'dateRange',
        type: 'dateRange',
        label: '조회기간',
        startKey: 'date1',
        endKey: 'date2',
        startPlaceholder: defaultRange.start,
        endPlaceholder: defaultRange.end,
      },
      {
        key: 'docType',
        type: 'select',
        label: '문서유형',
        options: formOptions,
      },
      {
        key: 'team',
        type: 'select',
        label: '기안부서',
        options: teamOptions,
      },
      {
        key: 'title',
        type: 'input',
        label: '문서제목',
        placeholder: '제목을 입력해 주세요.',
      },
      {
        key: 'name',
        type: 'input',
        label: '기안자',
        placeholder: '홍길동',
      },
    ];
  }, [formOptions, teamOptions, defaultRange]);

  const currentSection = useMemo(() => {
    switch (activeTab) {
      case 'approvalIng':
        return approvalIngSection;
      case 'futureIng':
        return futureIngSection;
      case 'done':
        return doneSection;
      default:
        return approvalIngSection;
    }
  }, [activeTab, approvalIngSection, futureIngSection, doneSection]);

  const { tableData, isLoading, isFetching, setSearchParams, approvalRefetch } = currentSection;

  const filteredAndSortedData = useMemo(() => {
    if (!Array.isArray(tableData)) return [];

    let filtered = [...tableData];
    if (appliedSearchValue) {
      filtered = filtered.filter((item) => {
        const title = item.title || '';
        return title.toLowerCase().includes(appliedSearchValue.toLowerCase());
      });
    }

    return filtered.sort((a, b) => {
      const dateA = a.date || '';
      const dateB = b.date || '';
      return sortValue === 'latest' ? dateB.localeCompare(dateA) : dateA.localeCompare(dateB);
    });
  }, [tableData, appliedSearchValue, sortValue]);

  const sortOptions = [
    { value: 'latest', label: '기안일 최신순' },
    { value: 'oldest', label: '기안일 오래된순' },
  ];

  const selectedSortLabel =
    sortOptions.find((opt) => opt.value === sortValue)?.label ?? '기안일 최신순';

  useEffect(() => {
    setIsSortOpen(false);
    setSortValue('latest');
    setSearchValue('');
    setAppliedSearchValue('');
    setFilterValues({
      date1: defaultRange.start,
      date2: defaultRange.end,
      docType: '',
      team: '',
      title: '',
      name: '',
    });
  }, [activeTab]);

  function handleKeyDown(event) {
    if (event.key === 'Enter') {
      // 검색 실행 (이미 useMemo에서 처리됨)
      // 엔터 눌럿을때만 검색되게 변경
      setAppliedSearchValue(searchValue);
    }
  }

  function applySearchParams(values) {
    const params = {};

    if (values.date1) params.date1 = values.date1;
    if (values.date2) params.date2 = values.date2;

    if (values.docType) params.formCode = Number(values.docType);
    if (values.team) params.teamCode = Number(values.team);
    if (values.title?.trim()) params.title = values.title.trim();
    if (values.name?.trim()) params.name = values.name.trim();

    setSearchParams(params);
    setIsFilterOpen(false);
  }

  // 빈 메시지
  const emptyMessageMap = {
    approvalIng: '미결 문서가 없습니다.',
    futureIng: '예결 문서가 없습니다.',
    done: '기결 문서가 없습니다.',
  };

  return (
    <div className={styles.container}>
      {/* 탭 */}
      <div className={styles.tabWrapper}>
        <button
          type="button"
          className={`${styles.tab} ${activeTab === 'approvalIng' ? styles.tabActive : ''}`}
          onClick={() => onTabChange('approvalIng')}
        >
          {approvalIngSectionLoading ? '미결함' : `미결함 (${approvalIngCount ?? 0})`}
        </button>
        <button
          type="button"
          className={`${styles.tab} ${activeTab === 'futureIng' ? styles.tabActive : ''}`}
          onClick={() => onTabChange('futureIng')}
        >
          예결함
        </button>
        <button
          type="button"
          className={`${styles.tab} ${activeTab === 'done' ? styles.tabActive : ''}`}
          onClick={() => onTabChange('done')}
        >
          기결함
        </button>
      </div>

      {/* ✅ 기존 DOM 구조 복원: listSection */}
      <div className={styles.listSection}>
        {/* 검색 */}
        <div className={styles.searchField}>
          <div className={styles.searchIcon}>
            <img src={searchIcon} alt="검색" width={19} height={19} />
          </div>
          <input
            type="text"
            className={styles.searchInput}
            placeholder="제목을 입력해 주세요."
            value={searchValue}
            maxLength={200}
            onChange={(e) => setSearchValue(e.target.value)}
            onKeyDown={handleKeyDown}
          />
          {searchValue && (
            <button
              type="button"
              className={styles.clearButton}
              onClick={() => {
                setSearchValue('');
                setAppliedSearchValue('');
              }}
              aria-label="검색어 초기화"
            >
              <img src={closeIcon} alt="삭제" width={16} height={16} />
            </button>
          )}
          <button
            type="button"
            className={styles.settingButton}
            onClick={() => setIsFilterOpen(true)}
          >
            <img src={searchFilterIcon} alt="필터" width={19} height={19} />
          </button>
        </div>

        {/* 정렬 + 새로고침 */}
        <div className={styles.sortWrapper}>
          <div className={styles.sortWrapperLeft} ref={sortDropdownRef}>
            <button
              type="button"
              className={styles.sortButton}
              onClick={() => setIsSortOpen(!isSortOpen)}
            >
              <span className={styles.sortLabel}>{selectedSortLabel}</span>
              <img
                src={chevronDownIcon}
                alt=""
                className={`${styles.sortChevron} ${isSortOpen ? styles.sortChevronOpen : ''}`}
              />
            </button>

            {isSortOpen && (
              <ul className={styles.sortDropdown}>
                {sortOptions.map((option) => (
                  <li key={option.value}>
                    <button
                      type="button"
                      className={`${styles.sortOption} ${
                        sortValue === option.value ? styles.sortOptionActive : ''
                      }`}
                      onClick={() => {
                        setSortValue(option.value);
                        setIsSortOpen(false);
                      }}
                    >
                      {option.label}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <button
            type="button"
            className={styles.refreshButton}
            onClick={() => approvalRefetch?.()}
            aria-label="새로고침"
          >
            <img src={refreshIcon} alt="" className={styles.refreshIcon} />
          </button>
        </div>

        {/* 리스트 (새로고침 시 F5와 동일한 로딩 UI 표시) */}
        <div className={styles.listWrapper}>
          {isFetching ? (
            <div className={styles.listLoadingContainer}>
              <CircularProgress size={32} className={styles.listLoadingSpinner} />
              <div className={styles.listLoadingMessage}>문서 목록을 불러오는 중입니다...</div>
            </div>
          ) : (
            <MobileApprovalList
              items={filteredAndSortedData}
              onItemClick={onItemClick}
              emptyMessage={emptyMessageMap[activeTab]}
              isLoading={isLoading}
              isFetching={isFetching}
            />
          )}
        </div>
      </div>

      {/* 필터 */}
      <MobileFilterSheet
        isOpen={isFilterOpen}
        onClose={() => setIsFilterOpen(false)}
        title={
          activeTab === 'approvalIng' ? '미결함' : activeTab === 'futureIng' ? '예결함' : '기결함'
        }
        filterFields={filterFields}
        filterValues={filterValues}
        onFilterChange={setFilterValues}
        onApply={(latestValues) => {
          // 만약 MobileFilterSheet가 onApply 호출 시 현재의 내부 값을 인자로 준다면 그것을 사용
          const valuesToApply = latestValues || filterValues;
          applySearchParams(valuesToApply);
        }}
        onReset={() =>
          setFilterValues({
            date1: defaultRange.start,
            date2: defaultRange.end,
            docType: '',
            team: '',
            title: '',
            name: '',
          })
        }
        onSelectFieldClick={(field, currentValue) => {
          setSelectField({ ...field, currentValue });
          setIsSelectOpen(true);
        }}
      />

      {/* 셀렉트 */}
      <MobileSelectScreen
        isOpen={isSelectOpen}
        onClose={() => {
          setIsSelectOpen(false);
          setSelectField(null);
        }}
        title={selectField?.label || '선택'}
        options={selectField?.options || []}
        value={selectField?.currentValue}
        onChange={(value) => {
          setFilterValues((prev) => ({ ...prev, [selectField.key]: value }));
          setIsSelectOpen(false);
          setSelectField(null);
        }}
      />
    </div>
  );
}

MobileApproverContent.propTypes = {
  activeTab: PropTypes.string,
  onTabChange: PropTypes.func,
  approvalIngSection: PropTypes.object.isRequired,
  futureIngSection: PropTypes.object.isRequired,
  doneSection: PropTypes.object.isRequired,
  onItemClick: PropTypes.func,
  formOptions: PropTypes.array,
  teamOptions: PropTypes.array,
  approvalIngCount: PropTypes.number,
  approvalIngSectionLoading: PropTypes.bool,
};

export default MobileApproverContent;
